import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus, GripVertical } from 'lucide-react';
import { tr } from '../../i18n';
import { SortableSection } from './SortableSection';
import { SortableRow } from './SortableRow';
import { getAllergens } from '../../utils/menuExtras';

export const ItemsEditor = ({
  draft,
  selectedSub,
  setSelectedSub,
  lang,
  t,
  updateItem,
  addItem,
  deleteItem,
  reorderItems,
  reorderSubs,
  uploadPhoto,
  resolveImg,
  translate,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const currentSection = draft.items[selectedSub];

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <aside className="bg-white border-[1.5px] border-black rounded-[16px] p-3 h-fit md:sticky md:top-4 md:z-10 md:max-h-[calc(100vh-2rem)] md:overflow-y-auto" data-testid="admin-sections-sidebar">
        <p className="font-bold text-black/50 text-[10px] uppercase tracking-widest mb-1 px-2">Sezioni</p>
        <p className="text-[10px] text-black/40 px-2 mb-3 flex items-center gap-1">
          <GripVertical size={11} /> {lang === 'it' ? 'Trascina per riordinare' : 'Drag to reorder'}
        </p>
        <div className="space-y-3">
          {draft.mainCategories.map((cat) => (
            <div key={cat.slug}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-black/40 px-2 mb-1">
                {tr(cat.name, lang)}
              </p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => {
                  const { active, over } = event;
                  if (!over || active.id === over.id) return;
                  const list = draft.subCategories[cat.slug] || [];
                  const oldIndex = list.findIndex((s) => s.slug === active.id);
                  const newIndex = list.findIndex((s) => s.slug === over.id);
                  if (oldIndex >= 0 && newIndex >= 0) reorderSubs(cat.slug, oldIndex, newIndex);
                }}
              >
                <SortableContext
                  items={(draft.subCategories[cat.slug] || []).map((s) => s.slug)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="space-y-1">
                    {(draft.subCategories[cat.slug] || []).map((s) => (
                      <SortableSection
                        key={s.slug}
                        id={s.slug}
                        active={selectedSub === s.slug}
                        label={tr(s.name, lang)}
                        onClick={() => setSelectedSub(s.slug)}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            </div>
          ))}
        </div>
      </aside>

      <section className="md:col-span-3 bg-white border-[1.5px] border-black rounded-[16px] p-5 md:p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl md:text-3xl text-black">{tr(currentSection?.title, lang)}</h2>
          <button onClick={() => addItem(selectedSub)} className="pill-btn text-sm">
            <Plus size={14} /> {t('newItem')}
          </button>
        </div>
        <p className="text-xs text-black/50 mb-5 flex items-center gap-1.5">
          <GripVertical size={13} />
          {lang === 'it' ? 'Trascina la maniglia a sinistra per riordinare i piatti. Ricordati di salvare!' : 'Drag the handle on the left to reorder items. Remember to save!'}
        </p>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const list = currentSection?.items || [];
            const oldIndex = list.findIndex((it) => it._id === active.id);
            const newIndex = list.findIndex((it) => it._id === over.id);
            if (oldIndex >= 0 && newIndex >= 0) reorderItems(selectedSub, oldIndex, newIndex);
          }}
        >
          <SortableContext
            items={(currentSection?.items || []).map((it) => it._id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-6">
              {(currentSection?.items || []).map((it, idx) => (
                <SortableRow
                  key={it._id}
                  id={it._id}
                  it={it}
                  idx={idx}
                  t={t}
                  updateItem={(i, field, value) => updateItem(selectedSub, i, field, value)}
                  deleteItem={(i) => deleteItem(selectedSub, i)}
                  uploadPhoto={uploadPhoto}
                  resolveImg={resolveImg}
                  translate={translate}
                  allergens={getAllergens(draft)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      </section>
    </div>
  );
};

export default ItemsEditor;
